if (!window.Eurus.loadedScript.has('highlight-text-with-image.js')) {
    window.Eurus.loadedScript.add('highlight-text-with-image.js');
    
    requestAnimationFrame(() => {
      document.addEventListener('alpine:init', () => {
        Alpine.data('xHighlightAnimation', () => ({
          initElements: null,
          animationFrameId: null,
          observer: null,
          scrollHandler: null,
          resizeHandler: null,
          animationVisible: false,
          hasStarted: false,
          window_height: window.innerHeight,

          load(el, rtl_check, equalLines, fullScreen = false, range) {
            this.initElements = this.separateWords(el);
            this.partitionIntoLines(el, rtl_check, equalLines, fullScreen, range);

            let lastWidth = window.innerWidth;
            this.resizeHandler = this.debounce(() => {
              if (!el.isConnected) return;
              this.window_height = window.innerHeight;
              if (window.innerWidth !== lastWidth) {
                lastWidth = window.innerWidth;
                this.partitionIntoLines(el, rtl_check, equalLines, fullScreen, range);
              } else if (this.scrollHandler) {
                this.scrollHandler();
              }
            });
            window.addEventListener("resize", this.resizeHandler, { passive: true });
          },

          debounce(func, timeout = 300){
            let timer;
            return (...args) => {
              clearTimeout(timer);
              timer = setTimeout(() => { func.apply(this, args); }, timeout);
            };
          },

          calRelativePos(el, rtl_check = false) {
            const elRect = el.getBoundingClientRect();
            const parentRect = el.parentElement.getBoundingClientRect();

            const pos = Math.round((elRect.left - parentRect.left) / parentRect.width * 100)
            
            return rtl_check ? Math.max(0, Math.min(100, 100 - pos)) : pos;
          },

          separateWords(container) {
            let elements = [];
          
            container.childNodes.forEach(node => {
              if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
                let words = node.textContent.match(/(\s+|[^\s-]+|[-])/g);
                words.forEach(word => {
                  if (word) {
                    let span = document.createElement("span");
                    span.textContent = word;
                    elements.push(span);  
                  }
                });
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                elements.push(node);
              }
            });

            return elements;
          },

          partitionIntoLines(container, rtl_check, equalLines, fullScreen = false, range) {
            container.innerHTML = "";
            this.initElements.forEach(el => container.appendChild(el));
            let maxHeight = 0;
          
            requestAnimationFrame(() => {
              let lines = [];
              let currentLine = [];
              let prevMid = null;
          
              this.initElements.forEach((el) => {
                let rect = el.getBoundingClientRect();
                if (rect.width === 0) return;

                if (maxHeight < rect.height) {
                  maxHeight = rect.height;
                }
                let midpoint = rect.bottom + (rect.top - rect.bottom) / 2;
    
                if (prevMid !== null && Math.abs(midpoint - prevMid) > 10) {
                  lines.push(currentLine);
                  currentLine = [];
                } 
                currentLine.push(el);
                prevMid = midpoint;
              });
  
              if (currentLine.length) {
                lines.push(currentLine);
              }

              let newInnerHTML = lines.map((lineElements) => {
                let div = document.createElement("div");
                if (equalLines) {
                  div.className = "text-highlight content-center relative inline-block text-[rgba(var(--colors-heading),0.3)]";
                  div.style.height = `${maxHeight + 8}px`;
                } else {
                  div.className = "text-highlight content-center relative inline-block text-[rgba(var(--colors-heading),0.3)] mb-0.5";
                }
                lineElements.forEach(el => div.appendChild(el.cloneNode(true)));
                return div.outerHTML;
              }).join(" ");
            
              container.innerHTML = newInnerHTML;  
              container.setAttribute("x-intersect.once.margin.300px", "startAnim($el, " + rtl_check + ", " + fullScreen + ", " + range + ")");
              if (this.hasStarted) this.startAnim(container, rtl_check, fullScreen, range);
            });
          },

          startAnim(el, rtl_check, fullScreen = false, range) {
            const lineElements = Array.from(el.children);
            if (!lineElements.length || !el.isConnected) return;
            this.hasStarted = true;
            this.window_height = window.innerHeight;
            let starts = [];
            let ends = [];

            if (fullScreen) {
              let offsetStart = el.offsetParent.parentElement.getBoundingClientRect().top + window.scrollY;

              const offsets = { 3000: -200, 2000: 200 };
              let offset = offsets[range] ?? 600;
              
              lineElements.forEach((element, index) => {
                if (index != 0) {
                  starts.push(ends[index - 1]);
                  ends.push(starts[index] + range);
                } else {
                  starts.push(offsetStart);
                  ends.push(offsetStart + range)
                }
              });
              el.offsetParent.parentElement.style.height = `calc(${ends[ends.length - 1] - starts[0] + range / 2 + offset}px)`
            } else {
              starts = [0.7];
              ends = [0.5];
              
              lineElements.forEach((element, index) => {
                if (index != lineElements.length - 1) {
                  const element_rect = element.getBoundingClientRect();
                  const element_height = Math.abs(element_rect.bottom - element_rect.top) / this.window_height;
                  let start = ends[index] + element_height;
                  starts.push(start);
                  ends.push(Math.max(start - 0.2, 0.2));
                }
              });
            }
            this.createObserver(el, rtl_check, starts, ends, fullScreen);
          },

          createObserver(el, rtl_check, starts, ends, fullScreen = false) {
            if (this.observer) this.observer.disconnect();
            if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
            if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
            this.scrollHandler = () => {
              if (this.animationVisible && el.isConnected) {
                this.updateHighlight(el, rtl_check, starts, ends, fullScreen);
              }
            };
            window.addEventListener('scroll', this.scrollHandler, { passive: true });
            const option = {
              root: null,
              rootMargin: '300px',
              threshold: 0
            };

            this.observer = new IntersectionObserver((entries) => {
              entries.forEach((entry) => {
                this.animationVisible = entry.isIntersecting;
                if (entry.isIntersecting) {
                  this.updateHighlight(el, rtl_check, starts, ends, fullScreen);
                } else {
                  if (this.animationFrameId) {
                    cancelAnimationFrame(this.animationFrameId);
                    this.animationFrameId = null;
                  }
                }
              });
            }, option);

            this.observer.observe(el);
          },
          
          updateHighlight(el, rtl_check = false, starts, ends, fullScreen = false) {
            const update = () => {
              this.animationFrameId = null;
              if (!el.isConnected) return;
              const measurements = Array.from(el.children).map((element, index) => {
                const elementRect = element.getBoundingClientRect();
                const highlights = Array.from(element.getElementsByClassName('highlight')).map((highlight) => ({
                  element: highlight,
                  relativePosition: this.calRelativePos(highlight, rtl_check)
                }));

                return {
                  element,
                  index,
                  elementRect,
                  width: elementRect.width,
                  highlights
                };
              });

              measurements.forEach(({ element, index, elementRect, width, highlights }) => {
                let value;
                const position = fullScreen
                  ? window.scrollY
                  : Math.max(Math.min((elementRect.top / this.window_height), 1), 0);

                if (fullScreen) {
                  if (position < starts[index]) {
                    value = 0;
                  } else if (position > ends[index]) {
                    value = 120;
                  } else {
                    value = 120 * (position - starts[index]) / (ends[index] - starts[index]);
                  }
                } else if (position > starts[index]) {
                  value = 0;
                } else if (position < ends[index]) {
                  value = 120;
                } else {
                  value = 120 * (position - starts[index]) / (ends[index] - starts[index]);
                }

                element.style.backgroundSize = `${value}%`;

                highlights.forEach(({ element: highlight, relativePosition }) => {
                  if (Math.round(value) > relativePosition) {
                    highlight.classList.add('highlight-anm-start');
                    highlight.classList.remove('highlight-anm-end');
                  } else {
                    highlight.classList.remove('highlight-anm-start');
                    highlight.classList.add('highlight-anm-end');
                  }
                });

                element.style.setProperty('--highlight-fill-stop', `${width * value / 100 - width * 0.2}px`);
                element.style.setProperty('--highlight-unfill-stop', `${width * value / 100}px`);
              });

            };

            if (!this.animationFrameId) {
              this.animationFrameId = window.requestAnimationFrame(update);
            }
          },
          destroy() {
            if (this.observer) this.observer.disconnect();
            if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
            if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
            if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
          }
        }));
      })
    });
  }
